import React, { useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import styles from './PictureDaySignup.module.scss';
// The SAME validators the access-code entry and look-up screens use. Imported,
// never re-implemented: a second email regex or a second definition of "valid
// phone" is how two screens start disagreeing about the same address.
import { isValidEmailFormat, suggestEmail } from '../../utils/emailValidation';
import { formatPhone, isValidPhoneFormat } from '../../utils/phoneValidation';

// "Tell me when my school's photos are ready", taken BEFORE picture day.
//
// ONE component, used by the entry screen and the registration page. The two
// screens ask for different things around it -- the entry screen knows only the
// school, the registration page already has the child's name -- so what varies
// is passed in and the signup itself is written once. Two copies of a form that
// posts to the same endpoint is how the two screens start disagreeing about
// what a signup means.
//
// It asks for ONE contact and a channel, because the server does: a phone and
// an email travelling together allowed (channel: 'email', phone: '209...'),
// a row nothing could ever send to.
const STRINGS = {
  en: {
    emailLabel: 'Email',
    // "MOBILE", not "Phone", and the channel is carried by that one word rather
    // than by a parenthetical promise. Two reasons beyond convention:
    //
    // The consent line under the button already states what we will send, so a
    // label that repeats it is spending the field's most prominent words on
    // something already said.
    //
    // And it steers away from landlines, which we cannot text at all -- send_mms
    // does a carrier lookup and refuses a non-mobile number, so a landline typed
    // here becomes a parent who signed up and never hears from us.
    phoneLabel: 'Mobile number',
    phonePlaceholder: '(209) 555-0147',
    emailPlaceholder: 'you@example.com',
    submit: 'Notify me',
    submitting: 'Signing you up…',
    // AT LEAST ONE, not both. Shown only when both boxes are empty; a filled
    // box that is malformed gets its own per-field message instead, because
    // "enter one of these" is unhelpful advice to someone who did.
    missingContact: 'Enter an email address or a phone number.',
    // Same wording as the access-code screen's, so a parent who mistypes an
    // address is told the same thing wherever they do it.
    badEmail: 'That doesn’t look like a complete email address.',
    badPhone: 'Please enter a 10-digit phone number.',
    emailSuggestionPrefix: 'Did you mean ',
    emailSuggestionSuffix: '?',
    consentEmail: 'By entering your address you agree to receive an email when photos are ready.',

    // SMS CONSENT (A2P 10DLC), derived from the register page's sentence in
    // Registration.jsx.
    //
    // It replaced "By entering your number you agree to receive a text when
    // photos are ready. Msg & data rates may apply." -- which carried 3 of the
    // 10 elements expected at the point of collection, and, worse, took consent
    // by IMPLICATION while our published terms say consent is given "by
    // providing your mobile number and checking the consent box". The screen
    // contradicted the terms it links to.
    //
    // ⚠️ REGISTER PAGE IS STILL ON THE LONG VERSION. Two collection points
    // behind one Twilio campaign should say the same thing; syncing
    // Registration.jsx to this wording is an open task, not an oversight.
    //
    // ⚠️ THIS SHORTENED WORDING HAS NOT BEEN THROUGH THE REVIEW the 76-word
    // original had. Treat it as a legal affirmation, same standing rule as
    // AffirmationText.jsx.
    //
    // STOP and HELP stay in English in both languages: they are the literal
    // keywords Twilio matches on, not words being read.
    //
    // SHORTENED from 76 words to 47, keeping all nine elements. What was cut is
    // detail that belongs in the LINKED terms rather than at the point of
    // collection:
    //
    //   "at the mobile number provided"        -- the box it sits under says so
    //   "marketing and informational"          -- carried by naming the categories
    //   "notifications when my student's        -- compressed to the three
    //    photos are ready to view, order/          categories that must be
    //    shipping/delivery updates, and            disclosed: photos, orders,
    //    occasional promotions and special         promotional offers
    //    offers"
    //   "Message"/"unsubscribe"                -- the standard "Msg"/"cancel"
    //                                             abbreviations carriers accept
    //
    // What CANNOT be cut, and is all still here: express agreement, that the
    // texts are recurring and automated, the brand, the message categories
    // (including that they are promotional -- omitting that understates what we
    // send), consent-not-a-condition, frequency, rates, STOP, HELP, and both
    // links. `/guarantee#sms-terms` carries the full detail.
    smsConsentBody: 'I agree to receive recurring automated texts from Shoob Photography about my '
      + 'student’s photos, order updates, and promotional offers. Consent isn’t a condition of '
      + 'purchase. Msg frequency varies. Msg & data rates may apply. Reply STOP to cancel, HELP '
      + 'for help. See our ',
    privacyPolicy: 'Privacy Policy',
    smsConsentAnd: ' and ',
    messagingTerms: 'Messaging Terms',
    // Offers the way OUT as well as the way forward, because a parent who will
    // not consent still has a usable path: an email-only signup.
    // No longer shown on screen -- the highlighted block and the "Required" tag
    // do that. Kept as the screen-reader announcement, because it is the only
    // place the way OUT (use email instead) can be stated.
    missingSmsConsent: 'Required: tick the box to agree to texts, or clear the mobile number and use email instead.',
    required: 'Required',
    done: 'You’re on the list. We’ll let you know as soon as photos are ready.',
    // Alex's rule reaching the parent: this school has two children by that
    // name, so we ask rather than pick. Deliberately not phrased as an error --
    // they are already signed up, and this only sharpens what we can send.
    ambiguous: 'More than one student at this school has that name. Add their student ID and we’ll send you straight to their photos.',
    studentIdLabel: 'Student ID',
    studentIdSubmit: 'Save',
    failed: 'That didn’t go through. Please check what you entered and try again.',
    // NAMES THE JOB, not the field under it. This heading is the first thing a
    // parent reads inside the card, and "Your student" left them typing a name
    // into a form whose purpose was never stated -- the screen above it gives a
    // date and a reassurance, neither of which says "this is a signup". The
    // registration page keeps its own field-group label because that page
    // already carries a purpose heading ("Picture Day Updates") of its own.
    signupHeading: 'Get notified when pictures are ready',
    // "Student's", not just "First name". These sit under a heading about
    // getting notified, so a bare "First name" reads as the PARENT's -- and a
    // parent's name in these boxes is a signup that can never be matched to a
    // child when the roster arrives, which is the exact failure this card was
    // built to stop.
    firstName: 'Student’s first name',
    lastName: 'Student’s last name',
    // BOTH facts inside the field itself: that it is optional, and what it buys.
    // They were a separate line under the box, which is a whole line of height
    // to say something the box could say on its own. Doubles as the accessible
    // label, so a screen reader hears the same sentence sighted users read.
    //
    // Measured, not guessed: 218px against the 264px a 320px phone leaves inside
    // this input. "for secure verification" was the first try and needed 316px --
    // it would have been silently clipped on every phone.
    studentIdOptional: 'Student ID (optional - for secure verification)',
    // The namesake case, told AT THE NAME FIELDS rather than after submitting.
    // Names the reason plainly -- a parent who reads "we need the ID" without
    // being told why assumes we are being difficult.
    sharedName: 'Another student at this school has this exact name, so we need the student ID to tell them apart.',
    studentIdRequired: 'Student ID (required)',
    missingStudentId: 'Please add the student ID so we send photos to the right child.',
    missingName: 'Please enter your student’s first and last name.',
  },
  es: {
    emailLabel: 'Correo electrónico',
    phoneLabel: 'Número de celular',
    phonePlaceholder: '(209) 555-0147',
    emailPlaceholder: 'usted@ejemplo.com',
    submit: 'Avísenme',
    submitting: 'Registrando…',
    missingContact: 'Ingrese un correo electrónico o un número de teléfono.',
    badEmail: 'Eso no parece una dirección de correo electrónico completa.',
    badPhone: 'Ingrese un número de teléfono de 10 dígitos.',
    emailSuggestionPrefix: '¿Quiso decir ',
    emailSuggestionSuffix: '?',
    consentEmail: 'Al ingresar su correo acepta recibir un aviso cuando las fotos estén listas.',

    // Translation of the reviewed English above, element for element. FLAGGED
    // FOR REVIEW: the register page is English-only, so this Spanish has no
    // approved original to copy from -- it needs the same sign-off the English
    // sentence got. Same standing rule as AffirmationText.jsx, where the
    // consent sentence is treated as a legal affirmation in both languages.
    // STOP and HELP deliberately untranslated: they are Twilio keywords.
    smsConsentBody: 'Acepto recibir mensajes de texto automatizados recurrentes de Shoob '
      + 'Photography sobre las fotos de mi estudiante, actualizaciones de pedidos y ofertas '
      + 'promocionales. El consentimiento no es condición para comprar. La frecuencia varía. '
      + 'Pueden aplicar tarifas de mensajes y datos. Responda STOP para cancelar, HELP para '
      + 'ayuda. Consulte nuestra ',
    privacyPolicy: 'Política de Privacidad',
    smsConsentAnd: ' y ',
    messagingTerms: 'Términos de Mensajería',
    missingSmsConsent: 'Requerido: marque la casilla para aceptar los mensajes de texto, o borre el número de celular y use el correo.',
    required: 'Requerido',
    done: 'Ya está en la lista. Le avisaremos en cuanto las fotos estén listas.',
    ambiguous: 'Más de un estudiante en esta escuela tiene ese nombre. Agregue su número de estudiante y lo enviaremos directamente a sus fotos.',
    studentIdLabel: 'Número de estudiante',
    studentIdSubmit: 'Guardar',
    failed: 'No se pudo completar. Revise los datos e intente de nuevo.',
    signupHeading: 'Reciba un aviso cuando las fotos estén listas',
    firstName: 'Nombre del estudiante',
    lastName: 'Apellido del estudiante',
    // "ID de estudiante" rather than "Número de estudiante": the longer form
    // plus the parenthetical needed 333px and would have been clipped.
    studentIdOptional: 'ID de estudiante (opcional - verificación segura)',
    sharedName: 'Otro estudiante en esta escuela tiene este mismo nombre, así que necesitamos el ID de estudiante para distinguirlos.',
    studentIdRequired: 'ID de estudiante (requerido)',
    missingStudentId: 'Agregue el ID de estudiante para enviar las fotos al niño correcto.',
    missingName: 'Ingrese el nombre y apellido de su estudiante.',
  },
};

const PictureDaySignup = ({
  schoolId, lang, student, onSignedUp, confirmInline, collectName,
}) => {
  const t = STRINGS[lang] || STRINGS.en;

  // A LINE EACH, no channel toggle. The toggle made the parent choose before
  // they could type, hid one channel behind a tap, and meant a parent happy to
  // give us both had no way to say so. `channel` is no longer sent at all --
  // the server derives it from which of these two arrived, so the client cannot
  // post a channel that contradicts the contacts beside it.
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [missingContact, setMissingContact] = useState(false);
  const [emailBad, setEmailBad] = useState(false);
  const [phoneBad, setPhoneBad] = useState(false);
  // "Did you mean gmail.com?" -- a domain/TLD typo the parent can accept with
  // one tap. Cheaper than a confirm-your-email field, and it catches the case a
  // format check cannot: an address that is perfectly well-formed and still
  // wrong. From the SAME suggestEmail the access-code screens use.
  const [emailSuggestion, setEmailSuggestion] = useState(null);

  // UNCHECKED BY DEFAULT and never pre-ticked -- a pre-ticked box is not consent
  // under A2P 10DLC, it is an assumption. Required only when a mobile number was
  // actually given: an email-only signup consents to no texts and must not be
  // asked to.
  const [smsConsent, setSmsConsent] = useState(false);
  const [missingSmsConsent, setMissingSmsConsent] = useState(false);

  const [studentId, setStudentId] = useState('');
  const [state, setState] = useState('idle');
  const [needsStudentId, setNeedsStudentId] = useState(false);
  const [followUpFailed, setFollowUpFailed] = useState(false);

  // THE ENTRY SCREEN COLLECTS THE NAME TOO. It shipped asking for a contact
  // and nothing else, on the reasoning that every extra field costs signups --
  // which traded away the only thing that makes the promise keepable. With no
  // name there is nothing to re-scan against when the roster arrives, so every
  // one of those parents reached photos-are-ready day unmatchable and got
  // "we need some additional information" instead of their photos and code.
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [ownStudentId, setOwnStudentId] = useState('');
  // Per FIELD, not one flag for the pair: the red border has to land on the box
  // that is actually empty, and "first and last name" as a single flag cannot
  // say which.
  const [firstNameBad, setFirstNameBad] = useState(false);
  const [lastNameBad, setLastNameBad] = useState(false);

  // THE NAMESAKE CASE, resolved BEFORE the signup rather than after it. When two
  // different children at a school share a name, binding to one is a coin flip
  // whose losing side mails a family a link to someone else's child -- so the
  // student id stops being optional for that one parent.
  //
  // It used to be handled the other way round: take the signup, then show a
  // "more than one student has that name" card asking for an id. That card
  // arrived after the parent believed they were finished, which is the worst
  // moment to ask for the one thing they may have to go and look up.
  const [sharedName, setSharedName] = useState(false);
  const [missingStudentId, setMissingStudentId] = useState(false);

  const nameComplete = firstName.trim().length > 0 && lastName.trim().length > 0;

  // What the parent typed here wins over a hint passed in, so the registration
  // page (which owns its own fields) is unaffected.
  const studentFields = collectName
    ? {
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      student_id: ownStudentId.trim() || undefined,
    }
    : {};

  // `baseURL: '/'` because the customer app sets a global axios baseURL of
  // /api/v1 and this route is not under it -- without this the POST goes to
  // /api/v1/picture_day_signups and 404s. The old registration page carried the
  // same override for the same reason. A mocked axios in the unit tests cannot
  // see this, which is why it is asserted there explicitly.
  // Sent as SEPARATE columns, and blank becomes undefined so an untouched box
  // is absent from the payload rather than posting '' -- the partial unique
  // indexes are `WHERE phone IS NOT NULL`, and an empty string is not null.
  const post = (extra = {}) => axios.post('/picture_day_signups', {
    school_id: schoolId,
    email: email.trim() || undefined,
    phone: phone.trim() || undefined,
    ...student,
    ...studentFields,
    ...extra,
  }, { baseURL: '/' });

  // Asks the server whether this school holds two DIFFERENT children by this
  // name, as soon as both name boxes have something in them.
  //
  // A POST for a read, deliberately: a GET would put a child's name in the URL
  // and from there into logs and history. Same `baseURL` override as the signup
  // POST, for the same reason.
  //
  // Failure is SILENT and leaves the id optional. This check only ever tightens
  // a requirement, so a network blip must not block a signup -- the server still
  // reports the ambiguity on submit, which is the backstop.
  const checkSharedName = () => {
    if (!collectName || !nameComplete) return;

    axios.post('/picture_day_signups/name_check', {
      school_id: schoolId,
      first_name: firstName.trim(),
      last_name: lastName.trim(),
    }, { baseURL: '/' })
      .then(({ data }) => setSharedName(Boolean(data.ambiguous)))
      .catch(() => {
        // DELIBERATELY LEAVES THE PREVIOUS ANSWER ALONE. Failing open means "do
        // not ADD a requirement", not "remove one".
        //
        // It used to `setSharedName(false)` here, which raced with the
        // server-side backstop and lost: a rejected `.then(...).catch(...)`
        // chain settles one microtask LATER than the submit handler, so the
        // backstop set the flag true and this immediately set it back to false.
        // The parent was told to add an id with no explanation of why.
        //
        // Nothing is leaked by not clearing: editing either name clears it.
      });
  };

  const submit = (event) => {
    if (event) event.preventDefault();
    if (state === 'saving') return;

    // EVERY PROBLEM AT ONCE, then one decision to stop.
    //
    // This used to check in stages and `return` at the first failure, which sent
    // a parent round the loop once per mistake: press, fix the name, press, fix
    // the email, press, tick the box. Worse, it hid problems -- an empty name
    // and an unticked box would report only the box, so the fields the parent
    // still had to fill looked fine.
    //
    // Every field that is wrong now lights up in the same pass.
    const hasEmail = email.trim().length > 0;
    const hasPhone = phone.trim().length > 0;

    // The child's name is REQUIRED on the screen that collects it. Not marked
    // with an asterisk -- an empty box that turns red the moment it matters
    // says the same thing without decorating the form for everyone who fills it
    // in correctly first time.
    const firstMissing = collectName && firstName.trim().length === 0;
    const lastMissing = collectName && lastName.trim().length === 0;

    const noContact = !hasEmail && !hasPhone;
    const emailInvalid = hasEmail && !isValidEmailFormat(email.trim());
    const phoneInvalid = hasPhone && !isValidPhoneFormat(phone);

    // ONLY when a number was actually typed. A parent signing up by email
    // consents to no texts and must never be asked to tick anything.
    const consentMissing = hasPhone && !smsConsent;

    // THE NAMESAKE GATE. Only for the parent whose child shares a name with a
    // different child at the same school -- for everyone else the id stays
    // optional. Without it we would bind by coin flip, and the losing side is a
    // family sent a link to somebody else's child.
    const idMissing = collectName && sharedName && ownStudentId.trim().length === 0;

    setFirstNameBad(firstMissing);
    setLastNameBad(lastMissing);
    setMissingContact(noContact);
    setEmailBad(emailInvalid);
    setPhoneBad(phoneInvalid);
    setMissingSmsConsent(consentMissing);
    setMissingStudentId(idMissing);

    if (firstMissing || lastMissing || noContact || emailInvalid
        || phoneInvalid || consentMissing || idMissing) return;

    setState('saving');
    post()
      .then(({ data }) => {
        setNeedsStudentId(Boolean(data.needsStudentId));

        // THE SERVER-SIDE BACKSTOP for the blur check, which fails open on
        // purpose. If the name check never got an answer -- offline, throttled,
        // or the roster changed between typing and pressing -- the server still
        // spots the namesake, and this is the last chance to ask.
        //
        // Surfaced the SAME way the up-front check does: the notice by the name
        // and a required id, in the form. Not a separate card, which is what
        // this rewrite removed. The signup itself is ALREADY SAVED at this
        // point, so a parent who gives up here still keeps their place -- we
        // just cannot yet say which child it is for.
        if (collectName && data.needsStudentId && ownStudentId.trim().length === 0) {
          setSharedName(true);
          setMissingStudentId(true);
          setState('idle');
          return;
        }

        setState('done');
        if (onSignedUp) onSignedUp(data);
      })
      // The signup is the promise; a failure here means we did NOT take it, so
      // the parent has to be told rather than shown a success they did not get.
      .catch(() => setState('failed'));
  };

  const submitStudentId = (event) => {
    if (event) event.preventDefault();
    if (!studentId.trim()) return;

    setFollowUpFailed(false);
    post({ student_id: studentId })
      .then(({ data }) => {
        setNeedsStudentId(Boolean(data.needsStudentId));
        // Hands the screen back once the id has settled it, which is what lets
        // the entry form finally show its thank-you. Without this the parent
        // answers the question and nothing visibly happens.
        if (!data.needsStudentId && onSignedUp) onSignedUp(data);
      })
      // NOT swallowed. The signup itself is already safe, but a parent who
      // typed an id and saw no response cannot tell whether we got it.
      .catch(() => setFollowUpFailed(true));
  };

  // `confirmInline: false` hands the confirmation to the screen above, which on
  // the entry form replaces the whole card with its Thank You. The registration
  // page keeps the inline version, because the form around it stays useful.
  //
  // NO `|| needsStudentId` ANY MORE. That clause existed to keep the
  // which-child follow-up reachable on the entry screen, and it meant a parent
  // whose child has a namesake got "You're on the list" plus a card asking for
  // an id, while everyone else got the Thank You -- two different endings to the
  // same action, and the odd one arriving after they believed they had finished.
  //
  // `collectName` screens now settle the namesake case at the name fields
  // instead (see `checkSharedName`), so the entry screen always ends on its
  // Thank You. The registration page owns its own name fields and has no such
  // check, so it still resolves ambiguity here -- which is why the follow-up
  // below is kept rather than deleted.
  if (state === 'done' && confirmInline) {
    return (
      <div className={styles.done} role="status">
        <p className={styles.doneMessage}>{t.done}</p>

        {needsStudentId && (
          <div className={styles.followUp}>
            <p className={styles.ambiguous}>{t.ambiguous}</p>
            <label htmlFor="signup-student-id">{t.studentIdLabel}</label>
            <div className={styles.followUpRow}>
              <input
                id="signup-student-id"
                type="text"
                value={studentId}
                onChange={(event) => setStudentId(event.target.value)}
                onKeyDown={(event) => { if (event.key === 'Enter') submitStudentId(event); }}
              />
              <button type="button" className={styles.secondaryButton} onClick={submitStudentId}>
                {t.studentIdSubmit}
              </button>
            </div>
            {followUpFailed && <p className={styles.failed}>{t.failed}</p>}
          </div>
        )}
      </div>
    );
  }

  // A DIV, NOT A FORM. This component is dropped inside the entry screen's
  // own <form>, and a nested form is invalid HTML -- React warns and the
  // browser flattens it, which is exactly what stopped the pre-picture-day
  // screen rendering at all. A shared component cannot know whether its host is
  // already a form, so it renders none and wires Enter itself.
  const submitOnEnter = (event) => {
    if (event.key === 'Enter') submit(event);
  };

  return (
    <div className={styles.signup}>
      {/* ABOVE the channel picker, because the parent thinks about their child
          first and how to be reached second -- and because a name asked for
          after a "Notify me" button reads as an afterthought. */}
      {collectName && (
        <div className={styles.studentBlock}>
          <p className={styles.signupHeading}>{t.signupHeading}</p>

          <label className={styles.srOnly} htmlFor="signup-first-name">{t.firstName}</label>
          <input
            id="signup-first-name"
            className={`${styles.contact} ${firstNameBad ? styles.contactBad : ''}`}
            type="text"
            autoComplete="off"
            placeholder={t.firstName}
            aria-invalid={firstNameBad || undefined}
            value={firstName}
            onChange={(event) => {
              setFirstName(event.target.value);
              if (firstNameBad) setFirstNameBad(false);
              // A changed name invalidates the previous answer, so drop it
              // rather than leave a stale "we need the ID" attached to a name
              // that is no longer typed.
              if (sharedName) setSharedName(false);
              if (missingStudentId) setMissingStudentId(false);
            }}
            onBlur={checkSharedName}
            onKeyDown={submitOnEnter}
          />

          <label className={styles.srOnly} htmlFor="signup-last-name">{t.lastName}</label>
          <input
            id="signup-last-name"
            className={`${styles.contact} ${lastNameBad ? styles.contactBad : ''}`}
            type="text"
            autoComplete="off"
            placeholder={t.lastName}
            aria-invalid={lastNameBad || undefined}
            value={lastName}
            onChange={(event) => {
              setLastName(event.target.value);
              if (lastNameBad) setLastNameBad(false);
              if (sharedName) setSharedName(false);
              if (missingStudentId) setMissingStudentId(false);
            }}
            onBlur={checkSharedName}
            onKeyDown={submitOnEnter}
          />

          {/* BETWEEN the name and the id, which is the order the parent reads in:
              here is why we are about to ask for something more. `role="status"`
              rather than `alert` -- nothing has gone wrong, we are explaining a
              requirement before they hit it. */}
          {sharedName && (
            <p className={styles.sharedName} role="status">{t.sharedName}</p>
          )}

          {/* The "optional, to verify" note lives IN the placeholder and in this
              label -- no separate hint line under the box. `aria-describedby` is
              gone with it: it pointed at that paragraph, and an aria reference
              to an element that no longer exists is worse than none. The label
              carries the same sentence, so nothing was lost for a screen
              reader.
              The placeholder flips to "(required)" for the namesake case, so the
              box itself stops claiming to be optional the moment it isn't. */}
          <label className={styles.srOnly} htmlFor="signup-own-student-id">
            {sharedName ? t.studentIdRequired : t.studentIdOptional}
          </label>
          <input
            id="signup-own-student-id"
            className={`${styles.contact} ${missingStudentId ? styles.contactBad : ''}`}
            type="text"
            autoComplete="off"
            placeholder={sharedName ? t.studentIdRequired : t.studentIdOptional}
            aria-required={sharedName || undefined}
            aria-invalid={missingStudentId || undefined}
            value={ownStudentId}
            onChange={(event) => {
              setOwnStudentId(event.target.value);
              if (missingStudentId) setMissingStudentId(false);
            }}
            onKeyDown={submitOnEnter}
          />

          {missingStudentId && (
            <p className={styles.fieldError} role="alert">{t.missingStudentId}</p>
          )}
          {/* The red borders above say WHICH box; this says what to do about it.
              One line for the pair rather than one per box, because two
              identical complaints stacked under two red fields is noise. */}
          {(firstNameBad || lastNameBad) && (
            <p className={styles.fieldError} role="alert">{t.missingName}</p>
          )}
        </div>
      )}

      {/* Email first, then phone. Email is the field a parent can complete from
          memory without reaching for anything, and it is the cheaper channel to
          send on -- so it leads. Both are visible at once: nothing is hidden
          behind a tap, and a parent willing to give us both can. */}
      <label className={styles.fieldLabel} htmlFor="signup-email">{t.emailLabel}</label>
      {/* Leading icons, same treatment and the same two Feather glyphs the
          access-code and Get-Access-Code screens use (see
          AccessCodeOnlyForm.module.scss `.inputWithIcon`). Copied rather than
          redrawn so a parent meets the same envelope and the same handset on
          every screen that asks for these.
          `aria-hidden` because they are decoration: the visible label already
          names the field, and a screen reader announcing "image" here adds
          nothing. */}
      <div className={styles.inputWithIcon}>
        <span className={styles.leadingIcon} aria-hidden="true">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
            <polyline points="22,6 12,13 2,6" />
          </svg>
        </span>
        <input
          id="signup-email"
          className={`${styles.contact} ${emailBad ? styles.contactBad : ''}`}
          // `email` / `tel` bring up the right keyboard on a phone, which is
          // where most of these are typed.
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder={t.emailPlaceholder}
          aria-invalid={emailBad || undefined}
          aria-describedby={emailBad ? 'signup-email-error' : undefined}
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            // Clears as they correct it, so the message describes the CURRENT
            // value. Re-checked on blur and again on submit either way.
            if (emailBad) setEmailBad(false);
            if (emailSuggestion) setEmailSuggestion(null);
            if (missingContact) setMissingContact(false);
          }}
          // ON BLUR, not only on submit. Checking solely at submit meant a
          // parent typed a broken address, moved to the next field, and got no
          // word of it until they pressed the button -- by which point they
          // have stopped thinking about the address. Same trigger the
          // access-code screens use.
          onBlur={() => {
            if (!email.trim()) return;
            setEmailBad(!isValidEmailFormat(email.trim()));
            setEmailSuggestion(suggestEmail(email.trim()));
          }}
          onKeyDown={submitOnEnter}
        />
      </div>
      {/* Error OR suggestion, never both: a malformed address has nothing worth
          suggesting yet, and a suggestion means the address parsed fine and is
          merely probably wrong. */}
      {emailBad && (
        <p className={styles.fieldError} id="signup-email-error" role="alert">{t.badEmail}</p>
      )}
      {!emailBad && emailSuggestion && (
        <p className={styles.emailSuggestion}>
          {t.emailSuggestionPrefix}
          {/* A BUTTON, not just text: the whole point is that accepting the fix
              costs one tap rather than a retype. type="button" so it cannot
              submit the host screen's form. */}
          <button
            type="button"
            className={styles.emailSuggestionAction}
            onClick={() => { setEmail(emailSuggestion); setEmailSuggestion(null); setEmailBad(false); }}
          >
            {emailSuggestion}
          </button>
          {t.emailSuggestionSuffix}
        </p>
      )}

      <label className={styles.fieldLabel} htmlFor="signup-phone">{t.phoneLabel}</label>
      <div className={styles.inputWithIcon}>
        <span className={styles.leadingIcon} aria-hidden="true">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {/* A HANDSET, not a receiver. The Feather "phone" glyph the
                access-code screens use draws an old desk-phone receiver, which
                sits oddly beside a field labelled "Mobile number" -- and the
                one number we cannot use here is a landline. Feather
                "smartphone", so it stays in the same icon set as the envelope
                above it.
                The zero-length line is the home-button dot: it renders as a
                round cap rather than a line. Same trick already used at
                AccessCodeOnlyForm.jsx:578. */}
            <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
            <line x1="12" y1="18" x2="12.01" y2="18" />
          </svg>
        </span>
        <input
          id="signup-phone"
          className={`${styles.contact} ${phoneBad ? styles.contactBad : ''}`}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder={t.phonePlaceholder}
          aria-invalid={phoneBad || undefined}
          aria-describedby={phoneBad ? 'signup-phone-error' : undefined}
          value={phone}
          // Formatted as they type, through the SAME helper the access-code
          // screens use -- so a number typed here and a number typed there reach
          // the server in one shape.
          onChange={(event) => {
            const next = formatPhone(event.target.value);
            setPhone(next);
            if (phoneBad) setPhoneBad(false);
            if (missingContact) setMissingContact(false);
            // CLEARING the box withdraws the tick. Consent is given "at the
            // mobile number provided", so a tick left standing while the number
            // it belonged to is deleted would let the next number inherit
            // consent nobody gave for it.
            //
            // Only on EMPTY, not on every edit: the box reformats on each
            // keystroke, so untick-on-change would clear the tick while a parent
            // was still correcting a digit of the number they just consented for.
            if (next.trim().length === 0) {
              setSmsConsent(false);
              setMissingSmsConsent(false);
            }
          }}
          // Same reasoning as the email field: told on leaving the box, not held
          // back until the button.
          onBlur={() => {
            if (!phone.trim()) return;
            setPhoneBad(!isValidPhoneFormat(phone));
          }}
          onKeyDown={submitOnEnter}
        />
      </div>
      {phoneBad && (
        <p className={styles.fieldError} id="signup-phone-error" role="alert">{t.badPhone}</p>
      )}

      {missingContact && (
        <p className={styles.failed} role="alert">{t.missingContact}</p>
      )}

      {/* ABOVE the button, because it gates it. The old wording sat BELOW the
          button, where a parent could complete the form without ever having it
          in view -- which is not a defensible place to put the sentence consent
          rests on.
          Shown only once a number has been typed: a parent giving an email alone
          is consenting to no texts and must not be asked to tick anything. */}
      {phone.trim().length > 0 && (
        <label className={styles.smsConsent} htmlFor="signup-sms-consent">
          <input
            id="signup-sms-consent"
            type="checkbox"
            checked={smsConsent}
            aria-invalid={missingSmsConsent || undefined}
            onChange={(event) => {
              setSmsConsent(event.target.checked);
              if (event.target.checked) setMissingSmsConsent(false);
            }}
          />
          <span>
            {/* MARKS THE SECTION rather than adding a sentence under it. The
                two-line "please tick the box, or clear the mobile number" was a
                third paragraph on an already-long card, saying what the
                highlighted block plus one word now say in place.
                Rendered inside the label so it is read out with the sentence it
                qualifies, and only when the tick is what is missing -- the box
                is not "required" for a parent who typed no number at all. */}
            {missingSmsConsent && <span className={styles.requiredTag}>{t.required}</span>}
            {t.smsConsentBody}
            {/* Real links, opened in a new tab so a parent reading the terms
                does not lose the form they have half-filled. `noopener` because
                target=_blank without it hands the new page a window handle. */}
            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">{t.privacyPolicy}</a>
            {t.smsConsentAnd}
            <a href="/guarantee#sms-terms" target="_blank" rel="noopener noreferrer">{t.messagingTerms}</a>
            .
          </span>
        </label>
      )}

      {/* Announced, not shown. The highlight and the "Required" tag carry this
          visually; a screen reader gets the whole instruction, including the way
          out (use email instead), which a colour and one word cannot convey. */}
      {missingSmsConsent && (
        <p className={styles.srOnly} role="alert">{t.missingSmsConsent}</p>
      )}

      <button type="button" className={styles.submit} onClick={submit} disabled={state === 'saving'}>
        {state === 'saving' ? t.submitting : t.submit}
      </button>

      {/* The email line stays informational and stays below: it is a courtesy
          note, not a gate, and email has no carrier consent regime behind it. */}
      {email.trim().length > 0 && <p className={styles.consent}>{t.consentEmail}</p>}

      {state === 'failed' && (
        <p className={styles.failed} role="alert">{t.failed}</p>
      )}
    </div>
  );
};

PictureDaySignup.propTypes = {
  confirmInline: PropTypes.bool,
  // Render the child's name (required) and student ID (optional) inside this
  // card. The registration page owns its own fields and passes `student`;
  // the entry screen has nowhere else to put them.
  collectName: PropTypes.bool,
  schoolId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  lang: PropTypes.string,
  // Optional hints. The registration page has them; the entry screen does not,
  // and the signup must work either way.
  student: PropTypes.shape({
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    grade: PropTypes.string,
    student_id: PropTypes.string,
  }),
  onSignedUp: PropTypes.func,
};

PictureDaySignup.defaultProps = {
  confirmInline: true,
  collectName: false,
  lang: 'en',
  student: {},
  onSignedUp: null,
};

export default PictureDaySignup;
